<template>
  <v-container fluid class="rq-page">
    <!-- ================= Header ================= -->
    <div class="rq-header">
      <a class="rq-back" @click="getBackList()">
        <v-icon size="20" color="#2A6F97">mdi-arrow-left</v-icon>
        <span class="ml-2">Back to requests</span>
      </a>
      <div class="rq-title-row">
        <div>
          <h1 class="rq-title">Time Off Request</h1>
          <div class="rq-subtitle">Review request details before making a decision</div>
        </div>
        <span class="rq-status-chip rq-chip-warning">
          <v-icon size="18" class="mr-2" color="#E08A1E">mdi-clock-outline</v-icon>
          Pending
        </span>
      </div>
    </div>

    <div class="rq-grid">
      <!-- ================= Left column ================= -->
      <div>
        <!-- Employee -->
        <div class="rq-card rq-section">
          <div class="rq-card-title">Employee</div>
          <div class="d-flex align-center mt-4">
            <div class="rq-avatar-lg">
              <img v-if="image_show" :src="leave_data.images" @error="image_show = ''" />
              <span v-else>{{ initials }}</span>
            </div>
            <div class="ml-5" style="min-width: 0;">
              <div class="rq-emp-name">{{ fullName || "-" }}</div>
              <div v-if="employeeCode" class="rq-muted">{{ employeeCode }}</div>
            </div>
          </div>

          <v-divider class="rq-divider my-4"></v-divider>

          <div class="rq-meta-row">
            <div class="rq-meta">
              <v-icon size="24" color="#6B7280">mdi-office-building-outline</v-icon>
              <div class="ml-3">
                <div class="rq-meta-label">Department</div>
                <div class="rq-meta-value">{{ departmentName || "-" }}</div>
              </div>
            </div>
            <div class="rq-meta">
              <v-icon size="24" color="#6B7280">mdi-briefcase-outline</v-icon>
              <div class="ml-3">
                <div class="rq-meta-label">Position</div>
                <div class="rq-meta-value">{{ positionName || "-" }}</div>
              </div>
            </div>
            <div class="rq-meta">
              <v-icon size="24" color="#6B7280">mdi-account-outline</v-icon>
              <div class="ml-3">
                <div class="rq-meta-label">Approver</div>
                <div class="rq-meta-value">You</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Request details -->
        <div class="rq-card rq-section mt-5">
          <div class="d-flex align-center flex-wrap" style="gap: 12px;">
            <div class="rq-card-title">Request details</div>
            <v-spacer></v-spacer>
            <span class="rq-total-chip">
              <v-icon size="18" color="#2A6F97" class="mr-2">mdi-clock-outline</v-icon>
              {{ totalChipText }}
            </span>
          </div>

          <div class="rq-detail-row mt-4">
            <div class="rq-detail">
              <v-icon size="24" :color="leaveTypeIcon.color">{{ leaveTypeIcon.icon }}</v-icon>
              <div class="ml-3">
                <div class="rq-meta-label">Leave type</div>
                <div class="rq-meta-value">{{ leaveTypeName }}</div>
              </div>
            </div>
            <div class="rq-detail-sep"></div>
            <div class="rq-detail">
              <v-icon size="24" color="#2A6F97">mdi-calendar-blank</v-icon>
              <div class="ml-3">
                <div class="rq-meta-label">Date</div>
                <div class="rq-meta-value">{{ dateText }}</div>
              </div>
            </div>
            <div class="rq-detail-sep"></div>
            <div class="rq-detail">
              <v-icon size="24" color="#2A6F97">mdi-clock-outline</v-icon>
              <div class="ml-3">
                <div class="rq-meta-label">Duration</div>
                <div class="rq-meta-value">{{ durationText }}</div>
              </div>
            </div>
            <template v-if="requestedText">
              <div class="rq-detail-sep"></div>
              <div class="rq-detail">
                <v-icon size="24" color="#2A6F97">mdi-file-document-outline</v-icon>
                <div class="ml-3">
                  <div class="rq-meta-label">Requested</div>
                  <div class="rq-meta-value">{{ requestedText }}</div>
                </div>
              </div>
            </template>
          </div>

          <div class="rq-label mt-5">Reason</div>
          <div class="rq-note-box" :class="{ 'rq-note-empty': !leave_data.description }">{{ leave_data.description ||
            "No reason
            provided." }}</div>

          <!-- Attachments -->
          <template v-if="oldpreview_list.length > 0">
            <div class="rq-label mt-5">Attachments</div>
            <div class="rq-preview-list">
              <div v-for="(item, index) in oldpreview_list" :key="index" class="rq-preview-item"
                @click="deleteItemConfirm(item)">
                <img :src="item" />
                <div class="rq-preview-zoom">
                  <v-icon size="16" color="#FFFFFF">mdi-magnify-plus-outline</v-icon>
                </div>
              </div>
            </div>

            <!-- PDF (เดิม - ซ่อนอยู่) -->
            <vue-html2pdf :show-layout="false" :float-layout="true" :enable-download="true" :preview-modal="false"
              :paginate-elements-by-height="1100" filename="meeting_vote_report" :pdf-quality="2"
              :manual-pagination="false" pdf-format="a4" pdf-orientation="portrait" @progress="onProgress($event)"
              @hasStartedGeneration="hasStartedGeneration()" @hasGenerated="hasGenerated($event)" ref="html2Pdf">
              <section slot="pdf-content">
                <v-row>
                  <v-col v-for="(item, index) in oldpreview_list" :key="index" class="d-flex child-flex" cols="12">
                    <v-img eager disabled :src="item" class="grey lighten-2 mt-3 mb-5" aspect-ratio="1" contain>
                    </v-img>
                  </v-col>
                </v-row>
              </section>
            </vue-html2pdf>
          </template>
        </div>

        <!-- Leave balance -->
        <div class="rq-card rq-section mt-5">
          <div class="rq-card-title">Leave balance</div>
          <div class="rq-balance-row mt-4">
            <div class="rq-balance-main">
              <div class="rq-balance-icon">
                <v-icon size="30" :color="leaveTypeIcon.color">{{ leaveTypeIcon.icon }}</v-icon>
              </div>
              <div class="ml-4">
                <div class="rq-meta-label">{{ leaveTypeName }}</div>
                <div class="rq-balance-value">
                  {{ balanceAfterHours === null ? "-" : formatHours(balanceAfterHours, true) + " available" }}
                </div>
              </div>
            </div>
            <div class="rq-detail-sep"></div>
            <div class="rq-balance-item">
              <div class="rq-meta-label">Before request</div>
              <div class="rq-meta-value">
                {{ balanceAfterHours === null ? "-" : formatHours(balanceAfterHours + requestHours, true) }}
              </div>
            </div>
            <div class="rq-detail-sep"></div>
            <div class="rq-balance-item">
              <div class="rq-meta-label">After approval</div>
              <div class="rq-meta-value">
                {{ balanceAfterHours === null ? "-" : formatHours(balanceAfterHours, true) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= Right column ================= -->
      <div class="rq-side">
        <!-- Decision -->
        <div class="rq-card rq-section">
          <div class="rq-card-title">Decision</div>
          <div class="rq-muted mt-2">Add a note for the employee (required when rejecting)</div>
          <v-textarea ref="description" v-model="leave_data.description_approve" placeholder="Write a note..." outlined
            rows="4" hide-details class="rq-input mt-3"></v-textarea>

          <v-btn depressed block color="#2A6F97" class="rq-btn rq-btn-primary mt-5" @click="onSave">
            Approve request
          </v-btn>
          <v-btn outlined depressed block class="rq-btn rq-btn-danger mt-3" @click="onReject">
            Reject request
          </v-btn>

          <div class="rq-note-line mt-4">
            <v-icon size="18" color="#6B7280">mdi-shield-alert-outline</v-icon>
            <span class="ml-2">This action will notify the employee.</span>
          </div>
        </div>

        <!-- Timeline -->
        <div class="rq-card rq-section mt-5">
          <div class="rq-card-title mb-4">Request timeline</div>
          <div class="rq-timeline">
            <div class="rq-tl-item">
              <div class="rq-tl-dot rq-tl-done">
                <v-icon size="16" color="#FFFFFF">mdi-file-document-outline</v-icon>
              </div>
              <div class="rq-tl-body">
                <div><b>Submitted</b><span v-if="requestedText"> · {{ requestedText }}</span></div>
                <div class="rq-muted">by {{ fullName || "-" }}</div>
              </div>
            </div>
            <div v-for="(step, i) in timelineSteps" :key="i" class="rq-tl-item">
              <div class="rq-tl-dot" :class="step.dotClass">
                <v-icon size="16" :color="step.iconColor">{{ step.icon }}</v-icon>
              </div>
              <div class="rq-tl-body">
                <div><b>{{ step.title }}</b><span v-if="step.time"> · {{ step.time }}</span></div>
                <div class="rq-muted">{{ step.sub }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= ดูรูปแนบ ================= -->
    <v-dialog v-model="dialogDelete" max-width="720">
      <div class="rq-image-dialog">
        <div class="d-flex align-center mb-3">
          <span class="rq-card-title" style="font-size: 16px;">Attachment</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="closeDelete">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <img :src="imageshowpopup" class="rq-image-full" />
      </div>
    </v-dialog>

    <!-- ================= Dialogs (เดิม) ================= -->
    <v-dialog v-model="dialogSave" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Are you sure ?</v-card-title>
        <br />
        <v-card-subtitle>
          <span class="black--text">Do you really want to send this leave request? This process cannot
            be undone.</span>
        </v-card-subtitle>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeSave">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="onSave">OK</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogReject" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Are you sure ?</v-card-title>
        <br />
        <v-card-subtitle>
          <span class="black--text">Do you really want to send this leave request? This process cannot
            be undone.</span>
        </v-card-subtitle>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeReject">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="onReject">OK</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
    <SuccessDialogPush :status="dialogAdd" :text_color="text_color" :title="title" :message="message" :link="link" />
  </v-container>
</template>

<script>
import api from "@/services/api";
import { server, imageUrl } from "@/services/constants";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import SuccessDialog from "@/components/cards/SuccessDialog";
import html2pdf from "html2pdf.js";
import VueHtml2pdf from "vue-html2pdf";
import unity from "@/unity/unity";
import moment from "moment";

export default {
  name: "approve-leave-confirm",
  components: { SuccessDialogPush, SuccessDialog, VueHtml2pdf },
  data(vm) {
    return {
      str_m_time: "",
      end_m_time: "",
      image_show: "",
      day_select: "multiple",
      leave_hours: [
        { text: "1 hour", value: 1 },
        { text: "2 hours", value: 2 },
        { text: "3 hours", value: 3 },
        { text: "Half day", value: 4 },
        { text: "5 hours", value: 5 },
        { text: "6 hours", value: 6 },
        { text: "7 hours", value: 7 },
        { text: "Full day", value: 8 },
      ],
      menu_start: false,
      menu_end: false,
      leave_start_hours: "",
      leave_end_hours: "",
      dialogConfirm: false,
      approve_end_flow_status: false,
      approve_detail_data: [],
      imageshowpopup: "",
      fullName: "",
      employee_id: "",
      dialogDelete: false,
      dialogSave: false,
      dialogReject: false,
      preview: null,
      image: null,
      preview_list: [],
      oldpreview_list: [],
      image_list: [],
      menu: false,
      menu2: false,
      search: "",
      toggleEnable: false,
      selectstatus: "",
      dropdownLeave: [],
      dataAll: [],
      mDataArray: [],
      amountShow: "",
      headers: [
        {
          text: "Period",
          value: "period",
          class: "bg-colorth tabletextwhite",
        },
        {
          text: "Leave Type",
          value: "leave_type",
          class: "bg-colorth tabletextwhite",
        },
        {
          text: "Quota (Day)",
          value: "quota_day",
          class: "bg-colorth tabletextwhite",
        },
        {
          text: "Used",
          value: "user_day",
          class: "bg-colorth tabletextwhite",
        },
        {
          text: "Balance (Day)",
          value: "balance",
          class: "bg-colorth tabletextwhite",
        },
      ],
      leave_data: {
        leaveType: 0,
        leaveTypeId: 0,
        approve_id_now: 0,
        startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        description: "",
        description_approve: "",
        userId: 0,
        departmentId: 0,
        companyId: 0,
        amount: 0,
        images: null,
        lq_id: 0,
      },
      dateCurrent: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      dialogAdd: false,
      text_color: "text-h5 green--text text-center",
      title: "green",
      message: "green",
      link: "",
      requestRaw: {}, // ข้อมูลใบลาจาก getListApproveByRequestID (ใช้แสดงผล)
    };
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },
  computed: {
    // ================= UI ใหม่ =================
    initials() {
      const parts = String(this.fullName || "").trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
    },
    // รหัสพนักงาน / แผนก / ตำแหน่ง (แสดงเมื่อ API ส่งมา)
    employeeCode() {
      const x = this.requestRaw || {};
      return x.employee_code || x.emp_code || x.employee_no || x.emp_id || "";
    },
    departmentName() {
      const x = this.requestRaw || {};
      return x.department_name || x.dep_name || x.department || "";
    },
    positionName() {
      const x = this.requestRaw || {};
      return x.position_name || x.pos_name || x.position || "";
    },
    leaveTypeName() {
      const t = this.dropdownLeave.find((d) => Number(d.leave_type_id) === Number(this.leave_data.leaveTypeId));
      return (t && t.leave_type) || this.leave_data.leaveType || "-";
    },
    leaveTypeIcon() {
      const t = String(this.leaveTypeName || "").toLowerCase();
      if (t.includes("sick")) return { icon: "mdi-thermometer", color: "#3C8D4F" };
      if (t.includes("vacation")) return { icon: "mdi-palm-tree", color: "#2A6F97" };
      if (t.includes("personal") || t.includes("business")) return { icon: "mdi-account-outline", color: "#6B4FA0" };
      return { icon: "mdi-calendar-blank-outline", color: "#6B7280" };
    },
    // ชั่วโมงที่ขอลา (amount เป็นนาที)
    requestHours() {
      return (Number(this.leave_data.amount) || 0) / 60;
    },
    totalChipText() {
      const t = this.requestHours;
      const d = Math.floor(t / 8);
      const h = Math.round((t - d * 8) * 100) / 100;
      return `${d} ${d === 1 ? "day" : "days"} · ${h} ${h === 1 ? "hour" : "hours"}`;
    },
    dateText() {
      const a = this.displayDate(this.leave_data.startDate);
      if (this.day_select === "single") return a;
      const b = this.displayDate(this.leave_data.endDate);
      return a === b ? a : `${a} - ${b}`;
    },
    durationText() {
      const txt = (v) => {
        const o = this.leave_hours.find((x) => Number(x.value) === Number(v));
        return o ? o.text : "-";
      };
      if (this.day_select === "single") return txt(this.leave_start_hours);
      if (this.day_select === "multiple") return `${txt(this.leave_start_hours)} / ${txt(this.leave_end_hours)}`;
      return this.formatHours(this.requestHours);
    },
    // วันที่ยื่นคำขอ (แสดงเมื่อ API ส่งมา)
    requestedText() {
      const x = this.requestRaw || {};
      const raw = x.created_at || x.createdat || x.create_date || x.createdAt;
      if (!raw) return "";
      const m = moment(raw);
      return m.isValid() ? m.format("D MMM YYYY, HH:mm") : "";
    },
    // ยอดคงเหลือของประเภทลานี้ (ชั่วโมง) = หลังอนุมัติ
    // หมายเหตุ: ระบบหักโควต้าตั้งแต่ตอนยื่นใบลา ยอดคงเหลือปัจจุบันจึงเท่ากับยอดหลังอนุมัติ
    balanceAfterHours() {
      const q = this.dataAll.find((d) => Number(d.leave_type_id) === Number(this.leave_data.leaveTypeId));
      if (!q || q.balance_raw === undefined || isNaN(q.balance_raw)) return null;
      return Number(q.balance_raw) * 8;
    },
    // ลำดับการอนุมัติ จาก approve_detail_data
    timelineSteps() {
      const me = String(localStorage.getItem(server.USER_ID));
      const list = this.approve_detail_data || [];
      if (!list.length) {
        return [{ title: "Pending review", sub: "Awaiting your review", icon: "mdi-clock-outline", iconColor: "#FFFFFF", dotClass: "rq-tl-current", time: "" }];
      }
      let currentFound = false;
      return list.map((item, i) => {
        const name =
          item.approver_name || item.fullname_eng || item.fullname || item.name_eng || item.name || `Approver level ${i + 1}`;
        const time = item.updated_at && moment(item.updated_at).isValid() ? moment(item.updated_at).format("D MMM YYYY, HH:mm") : "";
        if (Number(item.status) === 2) {
          return { title: "Approved", sub: `by ${String(item.approve_id) === me ? "you" : name}`, icon: "mdi-check", iconColor: "#FFFFFF", dotClass: "rq-tl-ok", time };
        }
        if (Number(item.status) === 3) {
          return { title: "Rejected", sub: `by ${String(item.approve_id) === me ? "you" : name}`, icon: "mdi-close", iconColor: "#FFFFFF", dotClass: "rq-tl-bad", time };
        }
        if (!currentFound) {
          currentFound = true;
          const mine = String(item.approve_id) === me;
          return { title: "Pending review", sub: mine ? "Awaiting your review" : `Waiting for ${name}`, icon: "mdi-clock-outline", iconColor: "#FFFFFF", dotClass: "rq-tl-current", time: "" };
        }
        return { title: "Next approver", sub: name, icon: "mdi-account-outline", iconColor: "#9CA3AF", dotClass: "rq-tl-next", time: "" };
      });
    },
    computedDateFromFormatted() {
      return unity.formatDate(this.leave_data.startDate);
    },
    computedDateToFormatted() {
      return unity.formatDate(this.leave_data.endDate);
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    await api.checkVersion();
    this.$store.state.global_dialog_push = false;
    this.$store.state.global_dialog = false;

    this.userId = localStorage.getItem(server.USER_ID);

    await this.loaddataLeaveUsed(this.$route.params.employee_id);
    await this.loadLeaveApproveDetail();

    this.$hideLoader();
  },

  methods: {
    async getBackList() {
      this.$router.push(`/approver-time-off`);
    },
    async loadLeaveApproveDetail() {
      // const res = await api.getListApproveDetailByRequestId(
      const res = await api.getListApproveDetailNoZeroIdByRequestId(
        this.$route.params.id,
      );

      this.approve_detail_data = res.data;

    },
    exportToPDF() {
      this.$refs.html2Pdf.generatePdf();
    },
    async onDialogSave() {
      this.dialogSave = true;
    },
    async onDialogReject() {
      this.dialogReject = true;
    },
    async onSave() {
      this.dialogSave = false;

      var count_approve = 0;
      var user_approve_id = 0;
      var approve_id = 0;
      var user_approve_id_next = 0;
      var now_index = 0;
      var next_index = 0;
      var next_status = false;
      var flow_end_status = false;

      count_approve = this.approve_detail_data.length;

      this.approve_detail_data.forEach(async (item, index) => {
        if (!next_status) {
          if (item.status == 1) {
            now_index = index;
            next_index = index + 1;
            next_status = true;
            user_approve_id = item.approve_id;
            approve_id = item.id;
          }
        }
      });
      //   approve_id = this.approval_status[next_index].approve_id;

      if (now_index == count_approve - 1) {
        flow_end_status = true;
        user_approve_id = localStorage.getItem(server.USER_ID);
      } else {
        user_approve_id_next = this.approve_detail_data[next_index].approve_id;
      }


      this.leave_data.userId = localStorage.getItem(server.USER_ID);

      var save_data = {
        leave_id: this.$route.params.id,
        approve_detail_id: approve_id,
        user_approve_id: user_approve_id,
        user_approve_id_next: user_approve_id_next,
        flow_end_status: flow_end_status,
        approval_remark: this.leave_data.description_approve,
      };


      // var body = {
      //   status: status,
      //   leave_type_id: this.leave_data.leaveTypeId,
      //   employee_id: this.employee_id,
      //   userId: localStorage.getItem(server.USER_ID),
      //   description_approve: this.leave_data.description_approve,
      // };

      this.$showLoader();

      // const res_h = await api.updateApprove(this.$route.params.id, body);
      const res_save = await api.saveApproval(save_data);
      // if (res_h.status == 200 || res_h.status == 201) {
      if (res_save.data.save_sta) {
        api.SendnotificationToMobile({
          user_id: parseInt(this.$route.params.employee_id),
          send_mode: "send_to_requester_approved",
        });

        this.$store.state.global_push_dialog = true;
        this.setupAlertDialogPush(
          true,
          "Time Off Approved",
          "You have approved this request.",
          "text-h5 green--text text-center",
          "/approver-time-off",
        );
      } else {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Information Saved",
          "บันทึกข้อมูลไม่สำเร็จ",
          "text-h5 red--text text-center",
        );
      }
      this.$hideLoader();
    },
    async onReject() {

      if (this.leave_data.description_approve == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Failed",
          "Please enter all the details.",
          "text-h5 red--text text-center",
        );
        this.$refs["description"].focus();
        return;
      }

      var request_master_id = this.$route.params.id;

      var save_data = {
        request_master_id: parseInt(request_master_id),
        user_action: parseInt(localStorage.getItem(server.USER_ID)),
        approval_remark: this.leave_data.description_approve,
      };

      this.$showLoader();

      const res_save = await api.saveReject(save_data);

      this.$hideLoader();

      if (res_save.data.save_sta) {
        try {
          const filenames = this.oldpreview_list
            .map(url => url.split('/').pop())
            .filter(Boolean); // ตัดค่า null, undefined, "" ออก

          await api.updatedata(
            this.$route.params.id,
            { images: "" },
          );

          for (const filename of filenames) {
            const datadel = {
              modulename: "Pro_file",
              filetype: "image",
              refid: "",
              filename: filename,
            };

            await api.deleteFileStore({
              params: datadel,
            });
          }
        } catch (error) {
          console.error("❌ Error deleting images:", error);
        }
        api.SendnotificationToMobile({
          user_id: parseInt(this.$route.params.employee_id),
          send_mode: "send_to_requester_rejected",
        });

        this.$store.state.global_push_dialog = true;
        this.setupAlertDialogPush(
          true,
          "Time Off Rejected",
          "You have rejected this request.",
          "text-h5 green--text text-center",
          "/approver-time-off",
        );
      } else {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Failed",
          "Save data Failed",
          "text-h5 red--text text-center",
        );
      }
    },

    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.link = link;
    },
    saveItemConfirm() {
      this.dialogSave = true;
    },
    closeSave() {
      this.dialogSave = false;
    },
    closeReject() {
      this.dialogReject = false;
    },
    deleteItemConfirm(data) {
      // this.indeximage = index;
      this.imageshowpopup = data;
      this.dialogDelete = true;
    },
    closeDelete() {
      this.indeximage = null;
      this.dialogDelete = false;
    },
    async removeimage(index) {
      this.preview_list.splice(this.indeximage, 1);
      this.image_list.splice(this.indeximage, 1);

      this.dialogDelete = false;
    },

    async loaddataLeaveUsed(id) {
      const result = await api.getListUseByUserIDV2byCompany(
        id,
        localStorage.getItem(server.COMPANYID),
      );
      // console.log("loaddataLeaveUsedloaddataLeaveUsedloaddataLeaveUsedloaddataLeaveUsed")
      // console.log(result.data)

      var data = [];

      result.data.forEach((item) => {
        var obj = {
          leave_type_id: 1,
          use_leave: "0.0625",
          leave_period_from: "2021-12-31T17:00:00.000Z",
          leave_period_to: "2022-12-30T17:00:00.000Z",
          leave_type: "ลาป่วย",
          quota_day: "30.0000",
          user_day: "0.0625",
          balance: "29.9375",
        };

        obj.use_leave = item.use_leave;
        obj.leave_period_from = item.leave_period_from;
        obj.leave_period_to = item.leave_period_to;
        obj.leave_type = item.leave_type;
        obj.period = item.period;
        obj.quota_day = this.quotaListConvert(item.quota_day);
        obj.user_day = this.quotaListConvert(item.user_day);
        obj.balance = this.quotaListConvert(item.balance);
        // ค่าดิบ ใช้คำนวณ Leave balance (UI ใหม่)
        obj.leave_type_id = item.leave_type_id;
        obj.balance_raw = Number(item.balance);

        data.push(obj);
      });

      this.dataAll = data;

      const result_dropsown = await api.getListByUserIDV3byCompany(
        id,
        localStorage.getItem(server.COMPANYID),
      );

      //       console.log("getListByUserIDV3byCompany:");
      // console.log(result_dropsown.data);
      this.dropdownLeave = result_dropsown.data;



      const res_data = await api.getListApproveByRequestID(
        this.$route.params.id,
      );

      // console.log(`getListApproveByRequestIDgetListApproveByRequestID`);
      //  console.log(res_data.data);
      this.mDataArray = res_data.data;

      this.mDataArray.forEach((x, index) => {
        this.requestRaw = x;
        this.leave_data.leaveTypeId = Number(x.leavetypeid);
        this.leave_data.approve_id_now = x.approve_id_now;

        // แปลง startdate และ enddate เป็นรูปแบบที่ถูกต้อง
        // สำหรับ v-date-picker ต้องการรูปแบบ YYYY-MM-DD
        this.leave_data.startDate = this.convertToISODate(x.startdate);
        this.leave_data.endDate = this.convertToISODate(x.enddate);

        this.day_select = x.type_count;

        // แก้ไขตัวแปรให้ตรงกับข้อมูลที่ได้จาก API (starthour ไม่ใช่ startHour)
        this.leave_start_hours = Number(x.starthour);
        this.leave_end_hours = Number(x.endhour);

        this.leave_data.description = x.description;
        this.leave_data.amount = x.amount;
        this.leave_data.leaveType = x.leave_type;
        this.leave_data.lq_id = x.lq_id;

        // แสดงเวลาในรูปแบบที่อ่านง่าย
        this.str_m_time = this.formatDateTime(x.startdate);
        this.end_m_time = this.formatDateTime(x.enddate);

        this.image_show = x.image;
        if (x.image == "" || x.image == null) {
          this.leave_data.images = imageUrl + "/personal.png";
        } else {
          this.leave_data.images = imageUrl + "/" + x.image;
        }

        if (x.fullName_eng != " ") {
          this.fullName = x.fullname_eng;
        } else {
          this.fullName = x.fullname;
        }
        this.employee_id = x.employee_id;

        let setiamggfile = x.preview_image ? x.preview_image.split(",") : [];
        if (!setiamggfile[0] || setiamggfile[0].trim() === "") {
        } else {
          setiamggfile.forEach((x) => {
            this.oldpreview_list.push(
              `${imageUrl}/${x}`,
            );
          });
        }
      });

      this.amountShow = this.quotaListConvert(this.leave_data.amount / 8);
    },

    // ================= UI helpers (ใหม่) =================
    displayDate(ymd) {
      if (!ymd) return "-";
      const m = moment(ymd, "YYYY-MM-DD");
      return m.isValid() ? m.format("D MMM YYYY") : ymd;
    },
    formatHours(total, showZero) {
      const t = Number(total) || 0;
      if (t <= 0) return showZero ? "0 days" : "-";
      const d = Math.floor(t / 8);
      const h = Math.round((t - d * 8) * 100) / 100;
      const parts = [];
      if (d > 0) parts.push(`${d} ${d === 1 ? "day" : "days"}`);
      if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
      return parts.join(" ");
    },

    // เพิ่ม helper methods ใหม่
    convertToISODate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      // แปลงเป็นรูปแบบ YYYY-MM-DD สำหรับ v-date-picker
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatDateTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);

      // Format: DD/MM/YYYY HH:mm
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    formatDateOnly(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },

    formatTimeOnly(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    quotaListConvert: function (H) {
      var d = 0;
      var h = 0;
      var m = 0;
      var tmp1 = parseFloat(H * 8);
      var tmp_final;

      var tmp_d = tmp1 / 480;

      d = Math.floor(tmp_d);
      h = Math.floor((tmp_d - d) * 8);
      m = Math.floor(((tmp_d - d) * 8 - h) * 60);

      tmp_final = `${d} Day ${h} Hour`;

      return tmp_final;
    },

    previewMultiImage: function (event) {
      var input = event.target;
      var count = input.files.length;
      var index = 0;
      if (input.files) {
        while (count--) {
          var reader = new FileReader();
          reader.onload = (e) => {
            this.preview_list.push(e.target.result);
          };
          if (input.files[index].type.split("/")[0] === "image") {
            this.image_list.push(input.files[index]);
            reader.readAsDataURL(input.files[index]);
          }
          index++;
        }
      }
    },
  },
};
</script>

<style scoped>
.hover-back {
  transition: color 0.3s ease;
  /* เพิ่มการเคลื่อนไหว */
}

.hover-back:hover {
  color: #6edbff !important;
  /* เปลี่ยนเป็นสีน้ำเงินเข้มเมื่อ hover */
}

.v-btn:not(.v-btn--round).v-size--default.buttonblue {
  background-color: #1d2442;
  color: white;
}

.v-textarea:not(.v-textarea--round).v-size--default.buttongreen {
  background-color: #09b534;
  color: white;
}

.textarea {
  border: 2px solid;
  border-color: #09b534;
}

.heightsize55 {
  height: 55px;
}

.heightsize80 {
  height: 80px;
}

.heightsize30 {
  height: 30px;
}

.inputsize {
  font-size: smaller;
}

.btnuploadfile {
  border: 2px solid;
}

.heightsize70formadvance {
  height: 70px;
}
</style>

<style scoped>
/* ================= Page ================= */
.rq-page {
  padding: 16px 32px 32px;
  color: #1F2937;
}

/* ดึงหัวข้อขึ้นไปอยู่ระดับเดียวกับ Header (เหมือนหน้า Request) */
.rq-header {
  margin-top: -72px;
}

.rq-back {
  position: relative;
  z-index: 6;
  /* อยู่เหนือ v-app-bar เพื่อให้คลิกได้ */
  display: inline-flex;
  align-items: center;
  color: #2A6F97 !important;
  font-size: 15px;
  cursor: pointer;
  text-decoration: none;
}

.rq-back:hover {
  text-decoration: underline;
}

.rq-title-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.rq-title {
  font-size: 30px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin: 14px 0 0;
}

.rq-subtitle {
  font-size: 15px;
  color: #4B5563;
  margin-top: 6px;
}

.rq-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 28px;
  margin-top: 24px;
  align-items: start;
}

/* ================= Card ================= */
.rq-card {
  background: #FFFFFF;
  border: 1px solid #E8ECF1;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.rq-form-body {
  padding: 24px;
}

.rq-side-card {
  padding: 24px;
}

.rq-card-title {
  font-size: 19px;
  font-weight: 700;
  color: #111827;
}

.rq-muted-sm {
  font-size: 13px;
  color: #6B7280;
}

.rq-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

/* ================= Status chip ================= */
.rq-status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.rq-status-chip-sm {
  padding: 3px 10px;
  font-size: 13px;
  border-radius: 4px;
}

.rq-chip-success {
  background: #E8F3EA;
  color: #2E7D32;
}

.rq-chip-danger {
  background: #FDE8E8;
  color: #C62828;
}

.rq-chip-warning {
  background: #FDF0DC;
  color: #E08A1E;
}

.rq-chip-info {
  background: #E6EEF7;
  color: #2A5F8F;
}

/* ================= Info grid ================= */
.rq-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 24px;
}

.rq-info-item {
  border: 1px solid #EEF1F4;
  border-radius: 8px;
  padding: 12px 16px;
  background: #FAFBFC;
}

.rq-info-label {
  font-size: 13px;
  color: #6B7280;
  margin-bottom: 4px;
}

.rq-info-value {
  font-size: 15px;
  font-weight: 500;
  color: #111827;
}

.rq-info-sub {
  font-weight: 400;
  color: #4B5563;
}

.rq-type-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ================= Total bar ================= */
.rq-total-bar {
  display: flex;
  align-items: center;
  background: #F2F8FC;
  border: 1px solid #DCEAF4;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  color: #374151;
}

.rq-total-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #2A6F97;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rq-accent {
  color: #2A6F97;
  font-weight: 600;
  font-size: 16px;
}

.rq-divider {
  border-color: #EEF1F4 !important;
}

/* ================= Notes ================= */
.rq-note-box {
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 14px;
  color: #111827;
  min-height: 48px;
  white-space: pre-wrap;
  word-break: break-word;
}

.rq-note-empty {
  color: #9CA3AF;
  font-style: italic;
}

/* ================= Attachments ================= */
.rq-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.rq-preview-item {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  cursor: zoom-in;
}

.rq-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rq-preview-zoom {
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.rq-image-dialog {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 16px 20px 20px;
}

.rq-image-full {
  display: block;
  width: 100%;
  max-height: 75vh;
  object-fit: contain;
  background: #F3F4F6;
  border-radius: 8px;
}

/* ================= Right column ================= */
.rq-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #2A6F97;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.rq-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rq-emp-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rq-sum-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #374151;
  padding: 8px 0;
}

.rq-sum-row> :last-child {
  text-align: right;
}

.rq-sum-row> :last-child:not(.rq-status-chip) {
  color: #111827;
}

/* ================= Responsive ================= */
@media (max-width: 1100px) {
  .rq-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .rq-page {
    padding: 8px 12px 24px;
  }

  .rq-header {
    margin-top: 0;
  }

  .rq-info-grid {
    grid-template-columns: 1fr;
  }
}

/* ================= Approve page (เพิ่ม) ================= */
.rq-section {
  padding: 22px 24px;
}

.rq-muted {
  font-size: 14px;
  color: #6B7280;
}

.rq-avatar-lg {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #E6F2F8;
  color: #2A6F97;
  font-weight: 600;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.rq-avatar-lg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rq-emp-name {
  font-size: 19px;
}

.rq-meta-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.rq-meta,
.rq-detail {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}

.rq-meta-label {
  font-size: 13px;
  color: #6B7280;
}

.rq-meta-value {
  font-size: 15px;
  font-weight: 500;
  color: #111827;
  margin-top: 2px;
}

.rq-total-chip {
  display: inline-flex;
  align-items: center;
  background: #E6F2F8;
  color: #2A6F97;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  padding: 6px 12px;
}

.rq-detail-row,
.rq-balance-row {
  display: flex;
  align-items: stretch;
  gap: 20px;
  flex-wrap: wrap;
}

.rq-detail {
  flex: 1 1 0;
  min-width: 140px;
}

.rq-detail-sep {
  width: 1px;
  background: #E5E7EB;
}

.rq-note-box {
  background: #F7F8FA;
}

.rq-balance-main {
  flex: 1.6 1 0;
  display: flex;
  align-items: center;
  min-width: 220px;
}

.rq-balance-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #E6F2F8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rq-balance-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.rq-balance-item {
  flex: 1 1 0;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* ===== Decision ===== */
.rq-input>>>.v-input__slot {
  border-radius: 6px !important;
  background: #FFFFFF !important;
}

.rq-input>>>fieldset {
  border-color: #D1D5DB !important;
}

.rq-input.v-input--is-focused>>>fieldset {
  border-color: #2A6F97 !important;
}

.rq-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  height: 46px !important;
  border-radius: 6px !important;
}

.rq-btn-primary {
  color: #FFFFFF !important;
}

.rq-btn-danger {
  border-color: #E5534B !important;
  color: #D93025 !important;
  background: #FFFFFF;
}

.rq-note-line {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #4B5563;
}

/* ===== Timeline ===== */
.rq-timeline {
  position: relative;
}

.rq-tl-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding-bottom: 18px;
}

.rq-tl-item:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 15px;
  top: 32px;
  bottom: 0;
  width: 2px;
  background: #E5E7EB;
}

.rq-tl-item:last-child {
  padding-bottom: 0;
}

.rq-tl-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rq-tl-done {
  background: #2A6F97;
}

.rq-tl-ok {
  background: #2E7D32;
}

.rq-tl-bad {
  background: #C62828;
}

.rq-tl-current {
  background: #E08A1E;
}

.rq-tl-next {
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
}

.rq-tl-body {
  margin-left: 12px;
  font-size: 14px;
  color: #111827;
  padding-top: 5px;
}

@media (max-width: 600px) {
  .rq-meta-row {
    grid-template-columns: 1fr;
  }

  .rq-detail-sep {
    display: none;
  }
}
</style>